class HeaderMegaMenus extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.listenerOptions = { signal: this.controller.signal };

    this.desktop = window.matchMedia('(min-width: 990px)');
    this.hoverAvailable = window.matchMedia('(any-hover: hover)');

    this.header = this.closest('.header-wrapper');
    this.backdrop = this.querySelector('[data-menu-backdrop]');
    this.menus = Array.from(
      this.querySelectorAll('details[data-hover-menu]')
    );

    this.activeMenu = null;
    this.hoveredMenu = null;
    this.closeTimer = null;

    this.menus.forEach((menu) => {
      const summary = menu.querySelector(':scope > summary');
      const panel = menu.querySelector(':scope > .mega-menu__content');
      const closeButton = panel?.querySelector('.mega-menu__close');

      if (panel?.id) {
        summary.setAttribute('aria-controls', panel.id);
      }

      this.syncExpanded(menu);

      menu.addEventListener(
        'pointerenter',
        (event) => this.onPointerEnter(event, menu),
        this.listenerOptions
      );

      menu.addEventListener(
        'pointerleave',
        (event) => this.onPointerLeave(event, menu),
        this.listenerOptions
      );

      menu.addEventListener(
        'focusin',
        () => this.cancelClose(),
        this.listenerOptions
      );

      menu.addEventListener(
        'focusout',
        (event) => this.onFocusOut(event, menu),
        this.listenerOptions
      );

      menu.addEventListener(
        'toggle',
        () => this.onToggle(menu),
        this.listenerOptions
      );

      closeButton?.addEventListener(
        'click',
        () => this.closeMenu(menu, true),
        this.listenerOptions
      );

      if (menu.open) this.openMenu(menu);
    });

    this.backdrop.addEventListener(
      'click',
      () => this.closeMenu(this.activeMenu, true),
      this.listenerOptions
    );

    this.ownerDocument.addEventListener(
      'keydown',
      (event) => this.onKeyDown(event),
      this.listenerOptions
    );

    this.ownerDocument.addEventListener(
      'click',
      (event) => this.onOutsideClick(event),
      this.listenerOptions
    );

    this.desktop.addEventListener(
      'change',
      () => this.onBreakpointChange(),
      this.listenerOptions
    );

    window.addEventListener(
      'resize',
      () => this.updateBackdrop(),
      this.listenerOptions
    );

    window.addEventListener(
      'scroll',
      () => this.updateBackdrop(),
      { ...this.listenerOptions, passive: true }
    );

    this.resizeObserver = new ResizeObserver(() => {
      this.updateBackdrop();
    });

    if (this.header) this.resizeObserver.observe(this.header);
  }

  disconnectedCallback() {
    this.cancelClose();
    this.controller.abort();
    this.resizeObserver.disconnect();
  }

  getSummary(menu) {
    return menu.querySelector(':scope > summary');
  }

  syncExpanded(menu) {
    this.getSummary(menu).setAttribute(
      'aria-expanded',
      String(menu.open)
    );
  }

  hasProtectedFocus(menu) {
    const focusedElement = this.ownerDocument.activeElement;

    if (!menu.contains(focusedElement)) return false;

    const summary = this.getSummary(menu);

    // Mouse clicks can focus the summary too. Only retain its
    // focus for keyboard use; focus inside the panel always wins.
    return (
      focusedElement !== summary ||
      summary.matches(':focus-visible')
    );
  }

  cancelClose() {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  scheduleClose(menu) {
    if (this.activeMenu !== menu) return;

    this.cancelClose();

    this.closeTimer = window.setTimeout(() => {
      if (
        this.hoveredMenu !== menu &&
        !this.hasProtectedFocus(menu)
      ) {
        this.closeMenu(menu);
      }
    }, 200);
  }

  openMenu(menu) {
    this.cancelClose();

    if (this.activeMenu && this.activeMenu !== menu) {
      this.closeMenu(this.activeMenu, true);
    }

    this.activeMenu = menu;
    menu.open = true;

    this.syncExpanded(menu);
    this.updateBackdrop();
  }

  closeMenu(menu, restoreFocus = false) {
    if (!menu) return;

    this.cancelClose();

    const summary = this.getSummary(menu);
    const focusedElement = this.ownerDocument.activeElement;
    const focusWasInPanel =
      menu.contains(focusedElement) && focusedElement !== summary;

    menu.open = false;
    this.syncExpanded(menu);

    if (this.activeMenu === menu) {
      this.activeMenu = null;
    }

    this.updateBackdrop();

    if (restoreFocus && focusWasInPanel) {
      summary.focus({ preventScroll: true });
    }
  }

  updateBackdrop() {
    const isOpen = Boolean(
      this.desktop.matches && this.activeMenu?.open
    );

    this.backdrop.hidden = !isOpen;

    if (!isOpen || !this.header) return;

    const headerBottom = Math.max(
      0,
      this.header.getBoundingClientRect().bottom
    );

    this.backdrop.style.setProperty(
      '--mega-menu-header-bottom',
      `${headerBottom}px`
    );
  }

  onPointerEnter(event, menu) {
    if (
      event.pointerType !== 'mouse' ||
      !this.desktop.matches ||
      !this.hoverAvailable.matches
    ) {
      return;
    }

    this.hoveredMenu = menu;
    this.cancelClose();

    // Hovering another trigger must not hide focused menu links.
    if (
      this.activeMenu &&
      this.activeMenu !== menu &&
      this.hasProtectedFocus(this.activeMenu)
    ) {
      return;
    }

    this.openMenu(menu);
  }

  onPointerLeave(event, menu) {
    if (event.pointerType !== 'mouse') return;

    if (this.hoveredMenu === menu) {
      this.hoveredMenu = null;
    }

    this.scheduleClose(menu);
  }

  onFocusOut(event, menu) {
    if (!menu.contains(event.relatedTarget)) {
      this.scheduleClose(menu);
    }
  }

  onToggle(menu) {
    this.syncExpanded(menu);

    if (menu.open) {
      this.openMenu(menu);
    } else if (this.activeMenu === menu) {
      this.closeMenu(menu, true);
    }
  }

  onKeyDown(event) {
    if (
      event.key !== 'Escape' ||
      event.defaultPrevented ||
      !this.activeMenu
    ) {
      return;
    }

    event.preventDefault();
    this.closeMenu(this.activeMenu, true);
  }

  onOutsideClick(event) {
    if (this.activeMenu && !this.contains(event.target)) {
      this.closeMenu(this.activeMenu, true);
    }
  }

  onBreakpointChange() {
    this.hoveredMenu = null;

    if (!this.desktop.matches) {
      this.closeMenu(this.activeMenu);
    }
  }
}

if (!customElements.get('header-mega-menus')) {
  customElements.define('header-mega-menus', HeaderMegaMenus);
}